// Pure helpers for the QA Live Monitor incident review panel.
//
// Extracted so the risky bits — the review state machine and the raw-enum → label
// maps — are unit-testable without mounting the whole page.

// Mirror of Qa::Incidents::ReviewService::TRANSITIONS
// (app/services/qa/incidents/review_service.rb). The backend still enforces every
// transition; this only drives the UI so a reviewer never sees a button that would
// just error. Keep in sync with that table if the backend one changes.
export const INCIDENT_TRANSITIONS = {
  open: [
    'acknowledged',
    'investigating',
    'confirmed',
    'passed_false_positive',
    'policy_exception',
  ],
  acknowledged: [
    'investigating',
    'confirmed',
    'passed_false_positive',
    'policy_exception',
    'open',
  ],
  investigating: [
    'confirmed',
    'passed_false_positive',
    'policy_exception',
    'acknowledged',
    'open',
  ],
  confirmed: ['resolved', 'open'],
  passed_false_positive: ['resolved', 'open'],
  policy_exception: ['resolved', 'open'],
  resolved: ['open'],
};

export const canTransition = (fromState, toState) =>
  (INCIDENT_TRANSITIONS[String(fromState)] || []).includes(String(toState));

const VERDICT_LABELS = {
  // Machine verdicts from the non-template detectors. Spelled out as the
  // machine's opinion because the agent-facing page shows them too, and a bare
  // "Flagged" reads like a finding rather than something still waiting on a human.
  flagged: 'Flagged by detector',
  detected: 'Detected by monitor',
  wrong_template: 'Wrong template used',
  no_reply: 'No reply sent',
  correct: 'Correct',
  ambiguous: 'Ambiguous match',
  policy_not_configured: 'Policy not configured',
  detector_error: 'Detector error',
  confirmed: 'Confirmed',
  passed_false_positive: 'Passed (false positive)',
  policy_exception: 'Policy exception',
};

const STATE_LABELS = {
  // "Open" was read as "the chat is still open" rather than "nobody has reviewed
  // this yet", which is what the state actually means.
  open: 'Pending review',
  acknowledged: 'Acknowledged',
  investigating: 'Investigating',
  confirmed: 'Confirmed',
  passed_false_positive: 'Passed (false positive)',
  policy_exception: 'Policy exception',
  resolved: 'Resolved',
};

const SOURCE_LABELS = {
  template_audit: 'Template audit',
  safety_language: 'Unsafe agent language',
};

// Title-case an unknown raw enum so it is at least readable rather than snake_case.
export const prettifyToken = value => {
  const text = String(value ?? '').trim();
  if (!text) return '—';
  return text.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

export const verdictLabel = value =>
  VERDICT_LABELS[String(value)] || prettifyToken(value);
export const reviewStateLabel = value =>
  STATE_LABELS[String(value)] || prettifyToken(value);
export const sourceLabel = value =>
  SOURCE_LABELS[String(value)] || prettifyToken(value);

// The one line of an incident card, chosen per source rather than uniformly.
//
// Most detectors write a title that says something no other field does —
// "Customer waiting too long", "Assigned without first reply" — and on A8 those
// are the majority of incidents, so replacing every title with the verdict label
// would turn 500 cards into "Detected". Only template_audit writes
// "<source label> · <raw verdict token>", which restates the source badge and
// then repeats the verdict in snake_case; there the verdict's own label is the
// better sentence. Anything else keeps its title.
export const incidentHeadline = item => {
  const title = String(item?.title || '').trim();
  const verdict = verdictLabel(item?.machine_verdict);
  return title.startsWith(`${sourceLabel(item?.source_type)} · `)
    ? verdict
    : title || verdict;
};

// Colour tone for the "latest review outcome" card, so a reviewer reads the result
// at a glance: green = machine was wrong / closed out, red = real issue upheld,
// amber = allowed by policy, grey = still in progress.
export const reviewOutcomeTone = state => {
  const value = String(state);
  if (value === 'passed_false_positive' || value === 'resolved') return 'good';
  if (value === 'confirmed') return 'danger';
  if (value === 'policy_exception') return 'warn';
  return 'muted';
};
